import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import RouteContainer from '../../../common/styles/RouteContainer'
import RouteTitle from '../../../common/RouteTitle/RouteTitle'
import booksitoutIcon from '../../../config/BooksitoutIcon'
import RouteTitleConfig from '../../../config/RouteTitleConfig'
import RowSpacer from '../../../common/styles/RowSpacer'
import useHtmlTitle from '../../../common/useHtmlTitle'
import useLoginStore from '../../login/useLoginStore'
import Login from '../../../common/Login'
import CardBodyContentContainer from '../../../common/styles/CardBodyContentContainer'
import { BooksitoutServer } from '../../../config/BooksitoutServer'
import BooksitoutImages from '../../../images/BooksitoutImages'

interface CalendarBook {
	id: number
	title: string
	cover: string | null
}

type CalendarDays = Record<string, CalendarBook[]>

const WEEKDAYS = ['월', '화', '수', '목', '금', '토', '일']

interface YearMonth {
	year: number
	month: number
}

const getPrevMonth = (ym: YearMonth): YearMonth =>
	ym.month === 1 ? { year: ym.year - 1, month: 12 } : { year: ym.year, month: ym.month - 1 }

const ymKey = (ym: YearMonth) => `${ym.year}-${String(ym.month).padStart(2, '0')}`

const BookCalendarRoute = () => {
	useHtmlTitle('독서 달력')
	const isLoggedIn = useLoginStore(state => state.isLoggedIn())

	const today = new Date()
	const currentYm: YearMonth = { year: today.getFullYear(), month: today.getMonth() + 1 }

	const [months, setMonths] = useState<YearMonth[]>([currentYm])
	const [calendarData, setCalendarData] = useState<Record<string, CalendarDays>>({})
	const fetchedRef = useRef<Set<string>>(new Set())
	const sentinelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!isLoggedIn) return
		months.forEach(ym => {
			const key = ymKey(ym)
			if (fetchedRef.current.has(key)) return
			fetchedRef.current.add(key)

			BooksitoutServer.get(`/v1/books/calendar?yearMonth=${key}`)
				.then(res => {
					setCalendarData(prev => ({ ...prev, [key]: res.data.days ?? {} }))
				})
		})
	}, [months, isLoggedIn])

	const checkAndLoadMore = useCallback(() => {
		const sentinel = sentinelRef.current
		if (!sentinel) return

		const rect = sentinel.getBoundingClientRect()
		if (rect.top < window.innerHeight + 500) {
			setMonths(prev => {
				const last = prev[prev.length - 1]
				return [...prev, getPrevMonth(last)]
			})
		}
	}, [])

	useEffect(() => {
		if (!isLoggedIn) return
		window.addEventListener('scroll', checkAndLoadMore, { passive: true })
		return () => window.removeEventListener('scroll', checkAndLoadMore)
	}, [isLoggedIn, checkAndLoadMore])

	useEffect(() => {
		if (!isLoggedIn) return
		requestAnimationFrame(checkAndLoadMore)
	}, [months, isLoggedIn, checkAndLoadMore])

	return (
		<RouteContainer>
			<RouteTitle icon={<booksitoutIcon.book />} title={'독서 달력'} currentKey={'calendar'} buttons={RouteTitleConfig.Book} />

			{!isLoggedIn ? (
				<>
					<RowSpacer />
					<CardBodyContentContainer style={{ paddingTop: '40px', paddingBottom: '40px' }}>
						<Login message={'로그인해 독서 달력 보기'} />
					</CardBodyContentContainer>
				</>
			) : (
				<>
					{months.map(ym => (
						<MonthCalendar
							key={ymKey(ym)}
							year={ym.year}
							month={ym.month}
							days={calendarData[ymKey(ym)] ?? {}}
							isCurrentMonth={ym.year === currentYm.year && ym.month === currentYm.month}
							today={today}
						/>
					))}
					<Sentinel ref={sentinelRef} />
					<RowSpacer />
				</>
			)}
		</RouteContainer>
	)
}

interface MonthCalendarProps {
	year: number
	month: number
	days: CalendarDays
	isCurrentMonth: boolean
	today: Date
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({ year, month, days, isCurrentMonth, today }) => {
	const firstDayOfMonth = (new Date(year, month - 1, 1).getDay() + 6) % 7
	const daysInMonth = new Date(year, month, 0).getDate()

	const getBooks = (day: number): CalendarBook[] => {
		const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
		return days[dateKey] ?? []
	}

	const cells: (number | null)[] = []
	for (let i = 0; i < firstDayOfMonth; i++) cells.push(null)
	for (let d = 1; d <= daysInMonth; d++) cells.push(d)

	return (
		<>
			<RowSpacer size={40} />
			<MonthLabel>{year}년 {month}월</MonthLabel>
			<RowSpacer size={10} />
			<CalendarGrid>
				{WEEKDAYS.map((day, i) => (
					<WeekdayHeader key={day} $isWeekend={i >= 5}>{day}</WeekdayHeader>
				))}

				{cells.map((day, i) => (
					<DayCell key={i} $isSeparator={i % 7 === 5}>
						{day != null && (
							<>
								<DayNumber $isToday={isCurrentMonth && day === today.getDate()}>{day}</DayNumber>
								<BooksContainer>
									{getBooks(day).map(book => (
										<BookCoverLink key={book.id} href={`/book/mine/${book.id}`}>
											<BookCover
												src={book.cover || BooksitoutImages.Placeholder.bookCover}
												alt={book.title}
											/>
										</BookCoverLink>
									))}
								</BooksContainer>
							</>
						)}
					</DayCell>
				))}
			</CalendarGrid>
		</>
	)
}

const MonthLabel = styled.h4`
	margin: 0;
	font-weight: bold;
	color: var(--text-color);
	text-align: center;
`

const CalendarGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 1px;
	background-color: var(--border-color);
	border: 1px solid var(--border-color);
	border-radius: 12px;
	overflow: hidden;
`

const WeekdayHeader = styled.div<{ $isWeekend?: boolean }>`
	text-align: center;
	padding: 10px 0;
	font-weight: bold;
	font-size: 13px;
	color: ${({ $isWeekend }) => $isWeekend ? 'var(--sub-text-color)' : 'var(--sub-text-color)'};
	background-color: var(--card-bg-color);
`

const DayCell = styled.div<{ $isSeparator?: boolean }>`
	min-height: 100px;
	padding: 6px;
	background-color: var(--card-bg-color);
	${({ $isSeparator }) => $isSeparator && 'border-left: 4px solid var(--primary-color);'}

	@media (max-width: 576px) {
		min-height: 70px;
		padding: 4px;
	}
`

const DayNumber = styled.span<{ $isToday: boolean }>`
	font-size: 12px;
	font-weight: ${({ $isToday }) => ($isToday ? 'bold' : 'normal')};
	color: ${({ $isToday }) => ($isToday ? 'var(--primary-color)' : 'var(--sub-text-color)')};
`

const BooksContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 3px;
	margin-top: 4px;
`

const BookCoverLink = styled.a`
	display: block;
`

const BookCover = styled.img`
	width: 100%;
	max-width: 40px;
	aspect-ratio: 2 / 3;
	object-fit: cover;
	border-radius: 3px;

	@media (max-width: 576px) {
		max-width: 28px;
	}
`

const Sentinel = styled.div`
	height: 1px;
`

export default BookCalendarRoute
